import { Button, cn, Icon, Menu, Skeleton } from '@starter/ui';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import {
  type BrandFieldName,
  type BrandFieldValue,
  useArchiveBrand,
  useBrand,
  useRestoreBrand,
  useSetColors,
  useSetLogo,
  useWriteField,
} from '../brands/api.ts';
import { ColorRoles } from '../brands/colors.tsx';
import { FieldRow, Panel } from '../brands/field-row.tsx';
import { fieldsOf, SECTIONS, type SectionId } from '../brands/fields.ts';
import { LogoDrop } from '../brands/logo.tsx';
import { ProvenanceBadge } from '../brands/provenance.tsx';
import { useCatalog } from '../catalogs/use-catalog.ts';
import { authClient } from '../lib/auth.ts';
import { provenanceKey } from '../lib/provenance-key.ts';
import { useSectionInView } from '../lib/section-in-view.ts';
import { useFeatureFlag } from '../observability/use-feature-flag.ts';

/**
 * A brand — the detail archetype from `scratchpad/brand`: one thing and its
 * sections, with the sections reachable from a nav that stays put.
 *
 * Every field carries the badge the server computed for it. The screen never
 * decides from `source === 'merchant'` what a person should read: that sentence
 * has to be the same here, on the product page, and in whatever language comes
 * next, and there is one place it can be (docs/BRAND.md).
 *
 * Archived is a *mood of this page*, not another page. The strip at the top
 * says so, every control below it goes read-only, and the address does not
 * change — an archived brand is still at its own URL, still readable, and one
 * button from being active again.
 */
function BrandDetail() {
  const { brandId } = Route.useParams();
  const brand = useBrand(brandId);
  const writeField = useWriteField(brandId);
  const setColors = useSetColors(brandId);
  const setLogo = useSetLogo(brandId);
  const archive = useArchiveBrand(brandId);
  const restore = useRestoreBrand(brandId);
  const roles = useCatalog('brand_color_roles');
  const verticals = useCatalog('brand_verticals');
  const extras = useFeatureFlag('brand-extra-fields');

  const [section, setSection] = useState<SectionId>('identity');

  /**
   * The nav scrolls to a section and follows the page back, the same way the
   * product's does — one hook, because two pages answering «which section am I
   * looking at» differently is two answers to one question.
   */
  const startedByClick = useSectionInView(SECTION_IDS, section, setSection, !brand.isPending);

  const go = (next: SectionId) => {
    startedByClick(next);
    document.getElementById(next)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const [conflict, setConflict] = useState<string | undefined>();
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');

  if (brand.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (brand.error || !brand.data) {
    return (
      <div className="flex flex-col items-start gap-4">
        <h1 className="font-display text-2xl font-bold text-ink">Brand not found</h1>
        <Button render={<Link search={{ status: 'active' }} to="/brands" />} variant="secondary">
          Back to my brands
        </Button>
      </div>
    );
  }

  const { brand: record, colors, provenance } = brand.data;
  const archived = record.status === 'archived';

  const save = (field: BrandFieldName) => (value: BrandFieldValue) => {
    setConflict(undefined);
    writeField.mutate(
      { field, value },
      {
        // 409 is the rule doing its job, not a failure of the screen: the field
        // belongs to someone else and the value on screen is still the true one.
        onError: (error) => setConflict(error.message),
      },
    );
  };

  return (
    <div className="flex flex-col gap-8">
      <Crumbs name={record.displayName} />

      <div className="flex flex-col gap-10 lg:flex-row">
        <SectionNav onChoose={go} section={section} />

        <div className="flex min-w-0 flex-1 flex-col gap-8">
          {archived ? (
            <div
              className="flex flex-wrap items-center gap-3 rounded-card border border-border-subtle bg-surface-raised px-4 py-3"
              data-testid="brand-archived-notice"
            >
              <span className="text-[16px] leading-[22.4px] text-ink">
                This brand is archived. Everything it holds comes back exactly as it was.
              </span>
              <Button
                data-testid="brand-restore"
                disabled={restore.isPending}
                onClick={() => restore.mutate()}
                variant="secondary"
              >
                Restore
              </Button>
            </div>
          ) : null}

          <header className="flex flex-wrap items-start gap-6">
            <LogoDrop
              error={setLogo.error?.message}
              name={record.displayName}
              onPick={(file) => setLogo.mutate(file)}
              pending={setLogo.isPending}
              readOnly={archived}
              uploadId={record.logoUploadId}
            />

            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {renaming ? (
                <form
                  className="flex flex-col gap-1"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (draftName.trim() === '') return;
                    save('displayName')(draftName.trim());
                    setRenaming(false);
                  }}
                >
                  <div className="flex flex-wrap items-start gap-2">
                    <input
                      ref={(element) => element?.focus()}
                      aria-label="Rename brand"
                      className="h-12 w-[420px] max-w-full rounded-control border border-border-subtle bg-surface px-4 text-[16px] leading-[22.4px] text-ink outline-none focus-visible:border-brand-400"
                      data-testid="brand-rename-input"
                      maxLength={200}
                      onChange={(event) => setDraftName(event.currentTarget.value)}
                      value={draftName}
                    />
                    <Button data-testid="brand-rename-save" type="submit" variant="primary">
                      Save
                    </Button>
                    <Button onClick={() => setRenaming(false)} type="button" variant="secondary">
                      Cancel
                    </Button>
                  </div>
                  <span className="text-bodysm leading-[16.8px] text-ink-muted">
                    New advertising will carry this name.
                  </span>
                </form>
              ) : (
                <div className="flex min-h-9 items-center gap-2">
                  <h1
                    className="min-w-0 truncate font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink"
                    data-testid="brand-title"
                  >
                    {record.displayName}
                  </h1>
                  <ProvenanceBadge
                    provenance={provenance[provenanceKey('brand', 'displayName')]}
                    testId="field-displayName-badge"
                  />
                  {archived ? null : (
                    <button
                      aria-label="Rename brand"
                      className="flex size-9 flex-none cursor-pointer items-center justify-center rounded-full text-ink-strong transition-colors hover:bg-surface-muted"
                      data-testid="brand-rename"
                      onClick={() => {
                        setDraftName(record.displayName);
                        setRenaming(true);
                      }}
                      type="button"
                    >
                      <Icon name="pencil" size={20} />
                    </button>
                  )}
                </div>
              )}
              <span
                className="text-bodysm leading-[16.8px] text-ink-muted"
                data-testid="brand-vertical"
              >
                {record.vertical ? verticals.label(record.vertical) : 'No vertical yet'}
              </span>
            </div>

            <Menu.Root>
              <Menu.Trigger
                aria-label="Actions for this brand"
                className="flex size-9 cursor-pointer items-center justify-center rounded-full text-ink-strong transition-colors hover:bg-surface-muted"
                data-testid="brand-actions"
              >
                <Icon name="ellipsis" size={20} />
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner align="end">
                  <Menu.Popup>
                    {archived ? (
                      <Menu.Item onClick={() => restore.mutate()}>Restore brand</Menu.Item>
                    ) : (
                      <Menu.Item data-testid="brand-archive" onClick={() => archive.mutate()}>
                        Archive brand
                      </Menu.Item>
                    )}
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          </header>

          {conflict ? (
            <p
              className="rounded-card border border-danger bg-surface-raised px-4 py-3 text-sm text-danger-text"
              data-testid="brand-conflict"
              role="alert"
            >
              {conflict}
            </p>
          ) : null}

          {/*
            Every section, one under the other, and the nav scrolls to them.
            It used to render exactly one and swap on click, which is not what
            the original does — theirs is a single long page with the list on
            the left, measured 2026-09-26 (`prd/measured/bridge-2026-09-25.md`).
            Swapping also loses your place: scroll into «Character», click
            «Colours», come back, and you are at the top again.
          */}
          {SECTIONS.map((entry) =>
            entry.id === 'look' ? (
              <Panel id="look" key={entry.id} testId="panel-look" title={entry.label}>
                <p className="text-bodysm text-ink-muted">
                  A palette is roles, not a list of swatches — one colour each, so a layout can ask
                  for the one the brand is recognised by.
                </p>
                <ColorRoles
                  colors={colors}
                  onChange={(next) => setColors.mutate(next)}
                  readOnly={archived}
                  roles={roles.items.map((role) => ({
                    key: role.key,
                    label: roles.label(role.key),
                  }))}
                />
              </Panel>
            ) : (
              <Panel id={entry.id} key={entry.id} testId={`panel-${entry.id}`} title={entry.label}>
                {fieldsOf(entry.id, extras).map((descriptor) => (
                  <FieldRow
                    error={undefined}
                    key={descriptor.name}
                    kind={descriptor.kind}
                    label={descriptor.label}
                    onSave={save(descriptor.name)}
                    options={
                      descriptor.catalog === 'brand_verticals'
                        ? verticals.items.map((item) => ({
                            key: item.key,
                            label: verticals.label(item.key),
                          }))
                        : undefined
                    }
                    pending={writeField.isPending}
                    placeholder={descriptor.placeholder}
                    provenance={provenance[provenanceKey('brand', descriptor.name)]}
                    readOnly={archived}
                    testId={`field-${descriptor.name}`}
                    value={record[descriptor.name]}
                  />
                ))}
              </Panel>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

/** Where you are: the list, then this brand. */
function Crumbs({ name }: { name: string }) {
  return (
    <header className="flex h-14 items-center gap-2 pr-2 pl-1">
      <Link
        className="text-[16px] leading-[22.4px] text-ink-mid no-underline hover:text-ink"
        search={{ status: 'active' }}
        to="/brands"
      >
        My brands
      </Link>
      <Icon className="text-ink-muted" name="chevron-right" size={24} />
      <span className="min-w-0 truncate text-[16px] leading-[22.4px] font-semibold text-ink">
        {name}
      </span>
    </header>
  );
}

/**
 * 200px, and it stays put while the page scrolls past it. The active row is a
 * coral fill at weight 600 — `scratchpad/block-section-nav` said the active
 * item changes weight; the product changes its colour too.
 */
function SectionNav({
  section,
  onChoose,
}: {
  section: SectionId;
  onChoose: (next: SectionId) => void;
}) {
  return (
    <nav className="flex h-fit w-50 flex-none flex-row gap-0.5 lg:sticky lg:top-4 lg:flex-col">
      <span className="hidden px-5 text-2xs leading-9 text-ink-muted lg:block">Brand</span>
      {SECTIONS.map((entry) => (
        <button
          // Which section is current is a fact, not only a fill — the same
          // attribute the product's nav carries.
          aria-current={entry.id === section ? 'true' : undefined}
          className={cn(
            'flex h-12 cursor-pointer items-center rounded-full px-5 text-left text-[16px] leading-[22.4px]',
            entry.id === section
              ? 'bg-brand-200 font-semibold text-ink'
              : 'font-normal text-ink hover:bg-surface-muted',
          )}
          data-testid={`brand-section-${entry.id}`}
          key={entry.id}
          onClick={() => onChoose(entry.id)}
          type="button"
        >
          {entry.label}
        </button>
      ))}
    </nav>
  );
}

/** The same ids the nav lists, in the order they come down the page. */
const SECTION_IDS = SECTIONS.map((entry) => entry.id);

export const Route = createFileRoute('/brands/$brandId')({
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: BrandDetail,
});
